'use client';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useAuth } from '@/contexts/AuthContext';

import {
  fetchRolePermissions,
  DEFAULT_PERMISSIONS,
  type TeamRole,
  type RolePermission,
} from '@/lib/services/userManagementService';

import {
  fetchUserPermissions,
  canAccessSubMenu,
  getSubMenuScope,
  type UserPermissionsMap,
} from '@/lib/services/userPermissionsService';

interface SubUserSession {
  id: string;
  username: string;
  fullName: string;
  role: TeamRole;
  storeId: string;
  salesPointId?: string | null;
}

interface PermissionsContextValue {
  role: TeamRole | 'super_admin' | null;
  permissions: Record<string, RolePermission>;
  /** Full user permissions map with sub-menu granularity (for sub-users) */
  userPermissions: UserPermissionsMap | null;
  loading: boolean;
  /** Returns true if the current user can perform the action on the module */
  can: (module: string, action: 'view' | 'add' | 'edit' | 'delete') => boolean;
  /** Returns true if the current user can access a specific sub-menu */
  canSub: (moduleKey: string, subMenuKey: string) => boolean;
  /** Returns the scope for a specific sub-menu ('own' | 'company' | null) */
  subScope: (moduleKey: string, subMenuKey: string) => 'own' | 'company' | null;
  /** Returns true if the current user is super_admin or admin */
  isAdminOrAbove: boolean;
  /** Returns true if the current user is super_admin AND NOT impersonating */
  isSuperAdmin: boolean;
  /** Sub-user session (if logged in as sub-user) */
  subUserSession: SubUserSession | null;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  role: null,
  permissions: {},
  userPermissions: null,
  loading: true,
  can: () => false,
  canSub: () => false,
  subScope: () => null,
  isAdminOrAbove: false,
  isSuperAdmin: false,
  subUserSession: null,
});

export const usePermissions = () => useContext(PermissionsContext);

function getSubUserSession(): SubUserSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('ef_subuser_session');
    if (!raw) return null;
    return JSON.parse(raw) as SubUserSession;
  } catch {
    return null;
  }
}

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const [rawRole, setRawRole] = useState<TeamRole | 'super_admin' | null>(null);
  const [permissions, setPermissions] = useState<Record<string, RolePermission>>({});
  const [userPermissions, setUserPermissions] = useState<UserPermissionsMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [subUserSession, setSubUserSession] = useState<SubUserSession | null>(null);

  // Access impersonation state — when active, super_admin acts as admin
  const { impersonation } = useImpersonation();
  const isImpersonating = impersonation.active;

  // Watch auth user changes to re-evaluate permissions on login/logout
  const { user: authUser, loading: authLoading } = useAuth();

  const load = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      // ── Sub-user path (no Supabase auth session) ──────────────────────────
      if (!user) {
        const session = getSubUserSession();
        if (session) {
          setSubUserSession(session);
          const userRole = session.role as TeamRole;
          setRawRole(userRole);

          // Fetch full user permissions with sub-menu granularity
          const fullPerms = await fetchUserPermissions(session.id, userRole);
          setUserPermissions(fullPerms);

          // Also build legacy permissions map for backward compat
          const legacyMap: Record<string, RolePermission> = {};
          for (const [key, mp] of Object.entries(fullPerms)) {
            legacyMap[key] = {
              module: key,
              canView: mp.canView,
              canAdd: mp.canAdd,
              canEdit: mp.canEdit,
              canDelete: mp.canDelete,
              scope: mp.scope,
            };
          }
          setPermissions(legacyMap);
          setLoading(false);
          return;
        }
        // No sub-user session either — clear state
        setSubUserSession(null);
        setRawRole(null);
        setPermissions({});
        setUserPermissions(null);
        setLoading(false);
        return;
      }

      // ── Regular Supabase auth user path ───────────────────────────────────
      // Clear any stale sub-user session
      if (typeof window !== 'undefined') {
        localStorage.removeItem('ef_subuser_session');
      }
      setSubUserSession(null);
      setUserPermissions(null);

      // Check if super_admin
      const { data: appUser } = await supabase
        .from('ef_app_users')
        .select('role')
        .eq('email', user.email)
        .maybeSingle();

      // Fallback: check by store owner (auth_user_id)
      let resolvedRole = appUser?.role as string | null;
      if (!resolvedRole) {
        const { data: storeOwner } = await supabase
          .from('ef_stores')
          .select('id')
          .eq('auth_user_id', user.id)
          .maybeSingle();
        if (storeOwner) {
          resolvedRole = 'admin'; // store owner always gets admin role
        }
      }

      // Normalize role
      const knownRoles: Array<TeamRole | 'super_admin'> = ['super_admin', 'admin', 'manager', 'cashier', 'warehouse'];
      const userRole: TeamRole | 'super_admin' | null = resolvedRole
        ? knownRoles.includes(resolvedRole as any)
          ? (resolvedRole as TeamRole | 'super_admin')
          : 'admin'
        : null;

      setRawRole(userRole);

      if (!userRole || userRole === 'super_admin') {
        setPermissions(DEFAULT_PERMISSIONS.admin);
        setLoading(false);
        return;
      }

      if (userRole === 'admin') {
        // Admin gets full permissions — no need to fetch from DB
        setPermissions(DEFAULT_PERMISSIONS.admin);
        setLoading(false);
        return;
      }

      // Fetch store-specific permissions for manager/cashier
      const allPerms = await fetchRolePermissions();
      setPermissions(allPerms[userRole as TeamRole] ?? DEFAULT_PERMISSIONS[userRole as TeamRole] ?? DEFAULT_PERMISSIONS.admin);
    } catch {
      setPermissions(DEFAULT_PERMISSIONS.admin);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-run load whenever the authenticated user identity changes (login / logout / sub-user switch)
  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authUser, authLoading, load]);

  // ── Effective role: during impersonation, super_admin acts as admin ────────
  const role: TeamRole | 'super_admin' | null = (rawRole === 'super_admin' && isImpersonating)
    ? 'admin'
    : rawRole;

  const can = useCallback(
    (module: string, action: 'view' | 'add' | 'edit' | 'delete'): boolean => {
      if (!role) return false;
      if (role === 'super_admin') return true;
      // Warehouse role: full access to warehouse module, limited elsewhere
      if (role === 'warehouse') {
        if (module === 'warehouse') return true;
        if (module === 'inventory' && (action === 'view' || action === 'add' || action === 'delete')) return true;
        return false;
      }
      // Super-admin-only module — non-super_admin users never have access
      if (module === '__super_admin__') return false;
      // Admin (including impersonating super_admin) has full CRUD access
      if (role === 'admin') return true;

      // For sub-users: use granular userPermissions if available
      if (userPermissions) {
        const up = userPermissions[module];
        if (!up) return false;
        switch (action) {
          case 'view': return up.canView;
          case 'add': return up.canAdd;
          case 'edit': return up.canEdit;
          case 'delete': return up.canDelete;
          default: return false;
        }
      }

      const perm = permissions[module];
      if (!perm) return false;
      switch (action) {
        case 'view': return perm.canView;
        case 'add': return perm.canAdd;
        case 'edit': return perm.canEdit;
        case 'delete': return perm.canDelete;
        default: return false;
      }
    },
    [role, permissions, userPermissions]
  );

  /** Check if user can access a specific sub-menu */
  const canSub = useCallback(
    (moduleKey: string, subMenuKey: string): boolean => {
      if (!role) return false;
      if (role === 'super_admin' || role === 'admin') return true;
      if (!userPermissions) return can(moduleKey, 'view');
      return canAccessSubMenu(userPermissions, moduleKey, subMenuKey);
    },
    [role, userPermissions, can]
  );

  /** Get scope for a specific sub-menu */
  const subScope = useCallback(
    (moduleKey: string, subMenuKey: string): 'own' | 'company' | null => {
      if (!role) return null;
      if (role === 'super_admin' || role === 'admin') return 'company';
      if (!userPermissions) {
        const perm = permissions[moduleKey];
        return perm?.scope ?? 'company';
      }
      return getSubMenuScope(userPermissions, moduleKey, subMenuKey);
    },
    [role, userPermissions, permissions]
  );

  const isAdminOrAbove = role === 'super_admin' || role === 'admin';
  // isSuperAdmin is false during impersonation so feature-flag filtering applies
  const isSuperAdmin = rawRole === 'super_admin' && !isImpersonating;

  return (
    <PermissionsContext.Provider value={{ role, permissions, userPermissions, loading, can, canSub, subScope, isAdminOrAbove, isSuperAdmin, subUserSession }}>
      {children}
    </PermissionsContext.Provider>
  );
}
